// Function that checks if the target numbers are included in the array of numbers chronologically
// Returns the amount of target numbers that are included in the array of numbers
export function progressCheck(targetArr, numberHistory) {

    let numPointer = 0;

    for (let i = 0; i < numberHistory.length; i++) {
        if (numberHistory[i] === targetArr[numPointer]) {
            numPointer++;
        }
    }

    return numPointer;
}

export const dateDifferenceInMiliSeconds = (dateInitial, dateFinal) =>
    dateFinal - dateInitial;

export const convertMillisToSeconds = (milliseconds) => {
    // Input validation
    if (typeof milliseconds !== 'number' || isNaN(milliseconds)) {
        console.error('Invalid input: milliseconds must be a number');
        return '0.00';
    }

    // Use absolute value and convert to seconds with two decimals
    const absMs = Math.abs(milliseconds);
    const seconds = absMs / 1000;
    return seconds.toFixed(2);
}

// Share/Copy text to clipboard with fallback support
export const shareOrCopyText = async (text) => {
    if (navigator.share) {
        await navigator.share({ text });
        return 'shared';
    } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return 'copied';
    } else {
        // Fallback for older browsers
        const tmp = document.createElement('textarea');
        tmp.value = text;
        document.body.appendChild(tmp);
        tmp.select();
        document.execCommand('copy');
        document.body.removeChild(tmp);
        return 'copied';
    }
}

// Game scoring constants (logistic time-based scoring)
const SCORING = {
    BASE_SCORE: 100,
    // Logistic time scoring parameters (seconds)
    TIME_MID_SECONDS: 50, // median / typical completion time
    TIME_CURVATURE: 1.4,  // controls steepness (1.2-1.6 recommended)
    // Step penalty parameter
    STEP_BETA: 0.2,
    // Weights for combining time vs steps
    W_TIME: 0.8,
    W_STEPS: 0.2,
    MIN_SCORE: 0,
    MAX_SCORE: 100,
};

export const calculateGameScore = (timeTakenMs, extraStepsTaken) => {
    const {
        BASE_SCORE,
        TIME_MID_SECONDS,
        TIME_CURVATURE,
        STEP_BETA,
        W_TIME,
        W_STEPS,
        MIN_SCORE,
        MAX_SCORE
    } = SCORING;

    const t = Math.max(0, timeTakenMs) / 1000; // seconds

    // Logistic-like time score: closer to 0 -> closer to 1; centered around TIME_MID_SECONDS
    const timeScore = 1 / (1 + Math.pow(t / TIME_MID_SECONDS, TIME_CURVATURE));

    // Step score: simple diminishing return with linear factor
    const stepCount = Math.max(0, extraStepsTaken);
    const stepScore = 1 / (1 + STEP_BETA * stepCount);

    // Weighted combination scaled to BASE_SCORE
    let score = BASE_SCORE * (W_TIME * timeScore + W_STEPS * stepScore);

    // Clamp and return rounded score
    score = Math.max(MIN_SCORE, Math.min(MAX_SCORE, score));
    return Math.round(score);
}

// Returns the index of the first operation that is incorrect
export const getIncorrectIndex = (operationHistory, solution, operators) => {
    for (let i = 0; i < operationHistory.length; i++) {
        if (operators[solution[i]] !== operationHistory[i]) {
            return i
        }
    }
    return -1
}

export const getNumHistoryFromSolution = (targetNums, operation, solution) => {
    const numHistory = [targetNums[0]];
    for (let i = 1; i < solution.length + 1; i++) {
        const newNumber = getNewNumber(numHistory[i - 1], operation[solution[i - 1]]);
        numHistory.push(newNumber);
    }
    return numHistory;
}

export const getNewNumber = (currentNumber, operator) => {
    const operatorNumber = (operator.startsWith('+') || operator.startsWith('-'))
        ? parseInt(operator, 10)
        : parseInt(operator.slice(1), 10);
    let newNumber;

    // Perform the operation based on the operator
    // i.e. if operator is '+1', then add 1 to the current number
    if (operator.startsWith('+') || operator.startsWith('-')) {
        newNumber = currentNumber + operatorNumber;
    } else if (operator.startsWith('*')) {
        newNumber = currentNumber * operatorNumber;
    } else if (operator.startsWith('/')) {
        if (currentNumber !== 0) {
            newNumber = currentNumber / operatorNumber;
        } else {
            console.error("Cannot divide by zero");
            return;
        }
    }
    return newNumber;
}

export const getOperationHistoryFromSolution = (operation, solution) => {
    const numHistory = [];
    for (let i = 0; i < solution.length; i++) {
        numHistory.push(operation[solution[i]]);
    }
    return numHistory;
}